import { useEffect, useId, useRef, useState } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Container } from "@/components/ui/container";
import { primaryNav, loginUrl, signupUrl } from "@/lib/content";

export function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuId = useId();
  const toggleRef = useRef<HTMLButtonElement>(null);

  // Close the mobile menu on Escape and restore focus to the toggle.
  useEffect(() => {
    if (!menuOpen) return;

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setMenuOpen(false);
        toggleRef.current?.focus();
      }
    }

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [menuOpen]);

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
      <a
        href="#main-content"
        className="sr-only-focusable fixed left-4 top-4 z-[60] rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"
      >
        Skip to content
      </a>
      <Container className="flex h-16 items-center justify-between gap-4">
        <a
          href="/"
          className="flex items-center gap-2 rounded-md text-base font-bold tracking-tight text-foreground"
        >
          <img
            src="/brand/logo-icon.svg"
            alt=""
            className="h-7 w-auto shrink-0"
            width={28}
            height={35}
          />
          <span>
            DataProtection<span className="text-primary">.work</span>
          </span>
        </a>

        <nav aria-label="Primary" className="hidden items-center gap-8 md:flex">
          {primaryNav.map((item) => (
            <a
              key={item.href}
              href={item.href}
              className="rounded-md text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="hidden items-center gap-2 md:flex">
          <Button variant="ghost" size="sm" asChild>
            <a href={loginUrl}>Log In</a>
          </Button>
          <Button variant="primary" size="sm" asChild>
            <a href={signupUrl}>Start Free</a>
          </Button>
        </div>

        <button
          ref={toggleRef}
          type="button"
          className="inline-flex h-10 w-10 items-center justify-center rounded-md text-foreground md:hidden"
          aria-expanded={menuOpen}
          aria-controls={menuId}
          onClick={() => setMenuOpen((open) => !open)}
        >
          {menuOpen ? <X className="h-6 w-6" aria-hidden="true" /> : <Menu className="h-6 w-6" aria-hidden="true" />}
          <span className="sr-only">{menuOpen ? "Close menu" : "Open menu"}</span>
        </button>
      </Container>

      {menuOpen && (
        <div id={menuId} className="border-t border-border md:hidden">
          <Container className="flex flex-col gap-1 py-4">
            <nav aria-label="Primary mobile" className="flex flex-col gap-1">
              {primaryNav.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={() => setMenuOpen(false)}
                  className="rounded-md px-2 py-2.5 text-base font-medium text-foreground hover:bg-secondary/60"
                >
                  {item.label}
                </a>
              ))}
            </nav>
            <div className="mt-3 flex flex-col gap-2 border-t border-border pt-3">
              <Button variant="outline" asChild>
                <a href={loginUrl}>Log In</a>
              </Button>
              <Button variant="primary" asChild>
                <a href={signupUrl}>Start Free</a>
              </Button>
            </div>
          </Container>
        </div>
      )}
    </header>
  );
}
